import { createContext, useContext, useState, useEffect, useCallback } from "react";
import { listarVentasUseCase } from "../../../core/facturacion/application/listarVentas.usecase.js";

/**
 * VentasContext
 * ----------------
 * Contexto de solo lectura para manejar ventas en la UI.
 * Permite:
 * - Acceder a todas las ventas cargadas
 * - Saber si están cargando (loading)
 * - Recargar ventas con filtros dinámicos
 */
const VentasContext = createContext();

// Hook para consumir el contexto
export const useVentas = () => useContext(VentasContext);

export function VentasProvider({ children }) {
  // Estado interno
  const [ventas, setVentas] = useState([]);    // Lista de ventas cargadas
  const [loading, setLoading] = useState(false); // Flag de carga

  /**
   * cargarVentas
   * ----------------
   * Función para traer ventas desde la capa de aplicación.
   * Recibe un objeto 'filtros', que puede contener:
   *   - estado: "pendiente" | "confirmada" | "cancelada"
   *   - numeroFactura: número específico de factura
   * Normaliza los pedidos para que siempre sean un array.
   * Memoizada con useCallback para evitar loops infinitos en useEffect.
   */
  const cargarVentas = useCallback(async (filtros = {}) => {
    setLoading(true);
    try {
      const data = await listarVentasUseCase(filtros);
      // Normalizamos pedidos para que siempre exista un array
      setVentas(data.map(f => ({ ...f, pedidos: f.pedidos || [] })));
      console.log("Ventas cargadas:", data);
    } catch (e) {
      console.error("Error cargando ventas", e);
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * useEffect de carga inicial
   * ---------------------------
   * Se ejecuta solo una vez al montar el provider.
   * Llama a cargarVentas() sin filtros para traer todas las ventas.
   * En componentes hijos, se puede llamar a cargarVentas({ estado: "confirmada" }) si se requiere.
   */
  useEffect(() => {
    cargarVentas();
  }, [cargarVentas]);

  // Contexto disponible para todos los hijos
  return (
    <VentasContext.Provider value={{ ventas, loading, cargarVentas }}>
      {children}
    </VentasContext.Provider>
  );
}
